import React, { useEffect, useRef, useState } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent } from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { tooltipFormatterDefault } from 'helpers/echart-utils';
import { PerformanceData } from 'pages/hvareporting/debtcollection/CampaignPerformanceReport';
import { format } from 'date-fns';

echarts.use([TooltipComponent, LineChart]);

interface EvolutionSeriesChartProps {
  data: PerformanceData[];
}

const EvolutionSeriesChart: React.FC<EvolutionSeriesChartProps> = ({
  data
}) => {
  // eslint-disable-next-line
  const { getThemeColor, addAlphaToHexColor, config: { isDark } } = useAppContext();
  const chartRef = useRef<ReactEChartsCore>(null);
  const [zoomState, setZoomState] = useState({ start: 0, end: 100 });

  const processData = (data: PerformanceData[]) => {
    const dates = data.map(item => format(new Date(item.date), 'MMM dd, yyyy'));
    // eslint-disable-next-line
    const activeCases = data.map(item => Number(item.campaigndata['Active Cases']?.value) || 0);
    // eslint-disable-next-line
    const customers = data.map(item => Number(item.campaigndata['Customers']?.value) || 0);
    // eslint-disable-next-line
    const totalDebt = data.map(item => Number(item.campaigndata['Total Debt']?.value) || 0);

    return { dates, activeCases, customers, totalDebt };
  };

  // eslint-disable-next-line
  const getDefaultOptions = (processedData: ReturnType<typeof processData>) => ({
    color: [
      getThemeColor('primary'),
      getThemeColor('success'),
      getThemeColor('gray-300')
    ],
    tooltip: {
      trigger: 'axis',
      // eslint-disable-next-line
      formatter: (params: any) =>
        tooltipFormatterDefault(params, 'MMM DD, YYYY', 'color'),
      // eslint-disable-next-line
      backgroundColor: !isDark ? 'rgba(240, 240, 240, 0.9)' : 'rgba(49, 55, 74, 0.9)',
      textStyle: {
        color: !isDark ? '#202020' : '#ccc',
        fontFamily: 'Nunito Sans',
        fontSize: 14
      },
      axisPointer: {
        shadowStyle: {
          color: 'red'
        }
      },
      // eslint-disable-next-line
      borderColor: !isDark ? 'rgba(49, 55, 74, 0.4)' : 'rgba(140, 140, 180, 0.8)'
    },
    legend: {
      top: '0',
      data: [
        { name: 'Active Cases', icon: 'roundRect' },
        { name: 'Customers', icon: 'roundRect' },
        { name: 'Total Debt', icon: 'roundRect' }
      ],
      itemWidth: 16,
      itemHeight: 8,
      itemGap: 10,
      inactiveColor: getThemeColor('quaternary-color'),
      inactiveBorderWidth: 0,
      textStyle: {
        color: getThemeColor('body-color'),
        fontWeight: 500,
        fontSize: 12,
        fontFamily: 'Nunito Sans'
      }
    },
    toolbox: {
      itemSize: 10,
      showTitle: false,
      show: true,
      iconStyle: {
        // eslint-disable-next-line
        borderColor: !isDark ? 'rgba(160, 160, 190, 0.9)' : 'rgba(100, 100, 150, 0.9)'
      },
      emphasis: {
        iconStyle: { borderColor: getThemeColor('primary') }
      },
      feature: {
        mark: { show: false },
        magicType: { show: false, type: ['line', 'bar'] },
        dataZoom: { show: false },
        dataView: { show: false, readOnly: true },
        restore: { show: false },
        saveAsImage: { show: true }
      }
    },
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: [0, 1],
        start: zoomState.start,
        end: zoomState.end,
        filterMode: 'filter',
        zoomOnMouseWheel: false,
        moveOnMouseWheel: false,
        moveOnMouseMove: true
      },
      {
        show: true,
        xAxisIndex: [0, 1],
        type: 'slider',
        left: '40',
        right: '75',
        top: '340',
        height: '20',
        start: zoomState.start,
        end: zoomState.end,
        fillerColor: addAlphaToHexColor(getThemeColor('primary'), 0.1),
        borderColor: addAlphaToHexColor(getThemeColor('primary'), 0.1),
        handleStyle: {
          color: getThemeColor('primary-light'),
          borderColor: getThemeColor('primary-light')
        },
        textStyle: {
          color: 'rgba(0, 0, 0, 0)',
          fontSize: 10
        },
        brushSelect: false
      }
    ],
    grid: {
      right: '15',
      left: '0',
      bottom: '26',
      top: '50',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: processedData.dates,
      axisLine: {
        lineStyle: {
          type: 'solid',
          color: getThemeColor('tertiary-bg')
        }
      },
      axisLabel: {
        color: getThemeColor('body-color'),
        margin: 10,
        rotate: 45,
        fontSize: 8.8
      },
      axisTick: {
        show: true,
        length: 5
      },
      splitLine: {
        interval: 0,
        show: false,
        lineStyle: {
          color: getThemeColor('tertiary-bg'),
          type: 'solid'
        }
      }
    },
    yAxis: [
      {
        type: 'value',
        name: 'Cases',
        position: 'left',
        nameTextStyle: {
          color: getThemeColor('body-color'),
          fontSize: 11,
          padding: [0, 0, 0, 30]
        },
        axisLine: {
          lineStyle: {
            type: 'solid',
            color: getThemeColor('tertiary-bg')
          }
        },
        axisLabel: {
          color: getThemeColor('body-color'),
          fontSize: 10.8
        },
        splitLine: {
          lineStyle: { color: getThemeColor('tertiary-bg') }
        }
      },
      {
        type: 'value',
        name: 'Total Debt',
        position: 'right',
        nameTextStyle: {
          color: getThemeColor('body-color'),
          fontSize: 11,
          padding: [0, 0, 0, -60]
        },
        axisLine: {
          lineStyle: {
            type: 'solid',
            color: getThemeColor('tertiary-bg')
          }
        },
        axisLabel: {
          color: getThemeColor('body-color'),
          fontSize: 10.8
        },
        splitLine: { show: false }
      }
    ],
    series: [
      {
        name: 'Active Cases',
        type: 'line',
        symbolSize: 6,
        data: processedData.activeCases
      },
      {
        name: 'Customers',
        type: 'line',
        symbolSize: 6,
        data: processedData.customers
      },
      {
        name: 'Total Debt',
        type: 'line',
        yAxisIndex: 1,
        symbolSize: 0,
        areaStyle: {
          opacity: 0.4
        },
        lineStyle: {
          width: 1
        },
        data: processedData.totalDebt
      }
    ]
  });

  useEffect(() => {
    if (chartRef.current) {
      const chart = chartRef.current.getEchartsInstance();
      const processedData = processData(data);

      chart.setOption(getDefaultOptions(processedData));

      chart.off('dataZoom');
      // eslint-disable-next-line
      chart.on('dataZoom', (params: any) => {
        setZoomState({
          start: params.start ?? zoomState.start,
          end: params.end ?? zoomState.end
        });
      });
    }
  }, [data]);

  return (
    <ReactEChartsCore
      ref={chartRef}
      echarts={echarts}
      option={getDefaultOptions(processData(data))}
      style={{ height: '360px', width: '100%' }}
    />
  );
};

export default EvolutionSeriesChart;
